import Autocomplete from '@betterfunnel/ui-core/Autocomplete';
import Button from '@betterfunnel/ui-core/Button';
import ChipsAutocomplete from '@betterfunnel/ui-core/ChipsAutocomplete';
import Modal, { ModalActionData } from '@betterfunnel/ui-core/Modal';
import Select from '@betterfunnel/ui-core/Select';
import TextField from '@betterfunnel/ui-core/TextField';
import Toggle from '@betterfunnel/ui-core/Toggle';
import {
    DynamicPage,
    HeaderParamMapping,
    NewEventIntegration,
    NewLeadIntegration,
    ObjectParamMapping,
    WebhookIntegration,
} from '@betterfunnel/types';
import {
    DialogTitle,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
} from '@headlessui/react';
import { ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { diff } from 'deep-diff';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuditLog } from '../contexts/AuditLogContext';
import { useDashboardContext } from '../contexts/DashboardContext';
import { useSnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import {
    createWebhookIntegration,
    updateIntegration,
} from '../services/IntegrationsService';
import { classNames, distinct } from '../utils';
import { formFieldUrlValidator, isValidUrl } from '../utils/validator';

interface Props {
  integrationId?: string;
  integration?: WebhookIntegration;

  open: boolean;
  onClose: () => void;
  onSaved?: () => void;

  forceDynamicPagesConnection?: string[];
}

type WebhookIntegrationData = Pick<
  WebhookIntegration,
  | 'name'
  | 'url'
  | 'authentication'
  | 'extraHeaders'
  | 'objectMapping'
  | 'status'
  | 'type'
  | 'dynamicPageIds'
  | 'contentType'
> &
  Partial<NewEventIntegration | NewLeadIntegration>;

export const EditWebhookIntegrationModal = ({
  open,
  onClose,
  onSaved,
  integrationId,
  integration,
  forceDynamicPagesConnection,
}: Props) => {
  const { show } = useSnackbarContext();
  const { selectedAccount } = useContext(UserContext);
  const { dynamicPages } = useDashboardContext();
  const auditLog = useAuditLog();

  const [data, setData] = useState<Partial<WebhookIntegrationData>>(
    integration ?? {},
  );

  const [eventTypeQuery, setEventTypeQuery] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const copy: Partial<WebhookIntegrationData> = integration
      ? JSON.parse(JSON.stringify(integration))
      : {};
    setData(
      copy
        ? {
            name: copy.name,
            url: copy.url,
            authentication: copy.authentication,
            extraHeaders: copy.extraHeaders,
            objectMapping: copy.objectMapping,
            status: copy.status,
            type: copy.type,
            dynamicPageIds: copy.dynamicPageIds,
            eventTypes: 'eventTypes' in copy ? copy.eventTypes : undefined,
            contentType: copy.contentType ?? 'json',
          }
        : {},
    );
  }, [integration]);

  const onSave = useCallback(() => {
    if (!selectedAccount) {
      return;
    }

    if (!data.url) {
      show('URL is required', 'warn');
      return;
    }

    if (!isValidUrl(data.url)) {
      show('Invalid URL', 'warn');
      return;
    }

    if (data.type === 'new_event' && !data.eventTypes?.length) {
      show('Event Types are required', 'warn');
      return;
    }

    setSaving(true);

    let promise: Promise<any>;
    if (integrationId) {
      promise = updateIntegration(selectedAccount.id, integrationId, data);
    } else {
      if (!data.url) {
        return;
      }
      promise = createWebhookIntegration(
        selectedAccount.id,
        data.type ?? 'new_lead',
        data.name ??
          (data.type === 'new_lead' ? 'New Lead Webhook' : 'Event Webhook'),
        data.url,
        data.objectMapping || [],
        data.extraHeaders || [],
        forceDynamicPagesConnection
          ? forceDynamicPagesConnection
          : data.dynamicPageIds || [],
        data.authentication,
        'eventTypes' in data ? data.eventTypes : undefined,
      );
    }

    promise
      .then(() => {
        onClose();
        onSaved?.();
        show(
          integrationId
            ? 'Webhook integration updated'
            : 'Webhook integration created',
          'success',
        );

        auditLog.log(
          integrationId ? 'update' : 'create',
          integrationId
            ? 'Update webhook integration'
            : 'Create webhook integration',
          'integration',
          integrationId,
          data.name,
          integration
            ? diff(integration, { ...integration, ...data })
            : undefined,
        );
      })
      .catch((e) => {
        show('Failed to save webhook integration', 'warn');
        console.error(e);
        Sentry.captureException(e);
      })
      .finally(() => {
        setSaving(false);
      });
  }, [
    auditLog,
    data,
    forceDynamicPagesConnection,
    integration,
    integrationId,
    onClose,
    onSaved,
    selectedAccount,
    show,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: integration ? 'Update' : 'Create',
        type: 'primary',
        loading: saving,
        onClick: () => onSave(),
      },
    ],
    [saving, integration, onClose, onSave],
  );

  return (
    <Modal open={open} onClose={onClose} actions={actions} width="3xl">
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        {integration ? 'Update Webhook' : 'Create Webhook'}
      </DialogTitle>
      <div className="mt-4 flex flex-col gap-4">
        <TextField
          title="Name"
          value={data.name}
          onChange={(e) => setData({ ...data, name: e.target.value })}
          placeholder='e.g. "New Lead Webhook"'
        />

        <Select<WebhookIntegration['type']>
          title="Trigger"
          disabled={!!integration}
          selected={data.type ?? 'new_lead'}
          onChange={(value) => setData({ ...data, type: value })}
          options={[
            { value: 'new_event', child: 'Event' },
            { value: 'new_lead', child: 'Lead' },
          ]}
        />

        {data.type === 'new_event' && (
          <ChipsAutocomplete<NonNullable<NewEventIntegration['eventTypes']>[0]>
            title="Event Types"
            selected={data.eventTypes ?? []}
            onChange={(value) => setData({ ...data, eventTypes: value })}
            getValue={(a) => (a === '*' ? 'All' : a)}
            onInputChange={(value) => setEventTypeQuery(value)}
            options={distinct(
              [
                '*',
                'view',
                'conversion',
                'button-click',
                eventTypeQuery,
              ].filter((x) => !!x),
            )}
          />
        )}

        <TextField
          title="URL"
          value={data.url}
          onChange={(e) => setData({ ...data, url: e.target.value })}
          validation={formFieldUrlValidator(data.url, true)}
          placeholder='e.g. "https://example.com/webhook"'
        />

        {!!integration && (
          <div>
            <label className="block pb-1 text-sm font-medium leading-6 text-gray-900">
              Status
            </label>
            <Toggle
              label={data.status === 'active' ? 'Active' : 'Disabled'}
              checked={data.status === 'active'}
              onChange={(value) =>
                setData({ ...data, status: value ? 'active' : 'disabled' })
              }
            />
          </div>
        )}

        {(forceDynamicPagesConnection === undefined || !!integration) && (
          <ChipsAutocomplete<QueryDocumentSnapshot<DynamicPage, DynamicPage>>
            title="Limit to specific Dynamic Pages"
            selected={
              data.dynamicPageIds
                ?.map((id) => dynamicPages?.find((p) => p.id === id))
                .filter((x) => !!x) ?? []
            }
            onChange={(value) =>
              setData({
                ...data,
                dynamicPageIds: value.length
                  ? value?.map((v) => v?.id)
                  : undefined,
              })
            }
            getValue={(a) => a.data().name}
            loading={dynamicPages === undefined}
            options={dynamicPages ?? []}
            disabled={forceDynamicPagesConnection !== undefined}
          />
        )}
        <Disclosure>
          {({ open }) => (
            <>
              <DisclosureButton
                className={classNames(
                  'flex w-full items-center gap-1 py-2',
                  open && 'border-b',
                )}
              >
                <span className="block text-sm font-medium leading-6 text-gray-900">
                  Advanced Settings
                </span>
                <div className="flex-1" />
                <ChevronRightIcon
                  className={classNames(
                    'h-4 w-4',
                    open ? 'rotate-90 transform' : '',
                  )}
                />
              </DisclosureButton>

              <DisclosurePanel>
                <div className="flex flex-col gap-4 divide-y divide-gray-200">
                  <div>
                    <div className="flex justify-between">
                      <h4 className="text-sm font-semibold leading-5 text-gray-900">
                        Object Mapping
                      </h4>
                    </div>

                    <ObjectMappingForm
                      type={data.type}
                      objectMapping={data.objectMapping ?? []}
                      onChange={(mapping) =>
                        setData({ ...data, objectMapping: mapping })
                      }
                    />
                  </div>

                  <div className="pt-2">
                    <Select<'apiKey' | 'jwt' | undefined>
                      title="Authentication Type"
                      selected={data.authentication?.type ?? 'jwt'}
                      onChange={(value) =>
                        setData({
                          ...data,
                          authentication:
                            value === 'apiKey'
                              ? {
                                  type: value,
                                  headerName: 'Authorization',
                                  value: '',
                                }
                              : undefined,
                        })
                      }
                      options={[
                        { value: 'apiKey', child: 'API Key' },
                        { value: 'jwt', child: 'Public/Private Key' },
                      ]}
                    />

                    {data.authentication?.type === 'apiKey' && (
                      <>
                        <TextField
                          title="Header Name"
                          value={data.authentication.headerName}
                          onChange={(e) =>
                            setData({
                              ...data,
                              authentication: {
                                value: data.authentication?.value ?? '',
                                type: 'apiKey',
                                headerName: e.target.value,
                              },
                            })
                          }
                        />
                        <TextField
                          title="API Key"
                          value={data.authentication.value}
                          onChange={(e) =>
                            setData({
                              ...data,
                              authentication: {
                                value: e.target.value,
                                type: 'apiKey',
                                headerName:
                                  data.authentication?.headerName ?? '',
                              },
                            })
                          }
                        />
                      </>
                    )}
                  </div>

                  <div className="pt-2">
                    <Select<'json' | 'form-data' | undefined>
                      title="Content Type"
                      selected={data.contentType ?? 'json'}
                      onChange={(value) =>
                        setData({
                          ...data,
                          contentType: value,
                        })
                      }
                      options={[
                        { value: 'json', child: 'JSON' },
                        { value: 'form-data', child: 'Form Data' },
                      ]}
                    />
                  </div>

                  <div className="pt-2">
                    <label className="block text-sm font-medium leading-6 text-gray-900">
                      Extra Headers
                    </label>
                    <div>
                      <ObjectMappingForm
                        objectMapping={data.extraHeaders ?? []}
                        onChange={(mapping) =>
                          setData({
                            ...data,
                            extraHeaders: mapping as HeaderParamMapping[],
                          })
                        }
                        allowedTypes={['custom', 'param']}
                      />
                    </div>
                  </div>
                </div>
              </DisclosurePanel>
            </>
          )}
        </Disclosure>
      </div>
    </Modal>
  );
};

const ObjectMappingForm = ({
  type,
  objectMapping,
  onChange,
  allowedTypes = ['custom', 'param', 'map'],
}: {
  type?: 'new_lead' | 'new_event';
  objectMapping: ObjectParamMapping[];
  onChange: (mapping: ObjectParamMapping[]) => void;
  allowedTypes?: ObjectParamMapping['value']['type'][];
}) => {
  const [parameterPathQuery, setParameterPathQuery] = useState('');
  const parameterPathOptions = useMemo(() => {
    return type === 'new_lead' ? leadKeys : eventKeys;
  }, [type]);

  return (
    <div className="pt-2">
      {objectMapping.map((item, index) => (
        <div
          className="my-2 flex w-full flex-col gap-2 rounded-lg border p-2"
          // eslint-disable-next-line react/no-array-index-key
          key={index}
        >
          <div className="flex w-full items-center gap-4">
            <TextField
              fullWidth
              placeholder="Key"
              value={item.key}
              onChange={(e) => {
                const key = e.target.value;
                if (objectMapping.some((m) => m.key === key)) {
                  return;
                }
                onChange(
                  objectMapping.map((m) =>
                    m.key === item.key ? { ...m, key } : m,
                  ),
                );
              }}
            />
            <div className="flex shrink-0 gap-2">
              <Select<'custom' | 'param' | 'map'>
                placeholder="Value Type"
                selected={item.value.type}
                onChange={(value) =>
                  onChange(
                    objectMapping.map((m) =>
                      m.key === item.key
                        ? {
                            ...m,
                            value: {
                              key: m.key,
                              type: value,
                              value: (value === 'map' ? [] : '') as any,
                            },
                          }
                        : m,
                    ),
                  )
                }
                options={[
                  { value: 'custom' as const, child: 'Custom Value' },
                  { value: 'param' as const, child: 'Parameter' },
                  { value: 'map' as const, child: 'Map' },
                ].filter((o) => allowedTypes.includes(o.value))}
              />
              <Button
                color="negative"
                onClick={() =>
                  onChange(objectMapping.filter((m) => m.key !== item.key))
                }
              >
                Remove
              </Button>
            </div>
          </div>
          {item.value.type === 'custom' && (
            <TypedTextField
              value={item.value.value}
              onChange={(value) =>
                onChange(
                  objectMapping.map((m) =>
                    m.key === item.key
                      ? {
                          ...m,
                          value: {
                            key: m.key,
                            type: 'custom',
                            value,
                          },
                        }
                      : m,
                  ),
                )
              }
            />
          )}
          {item.value.type === 'param' && (
            <>
              <Autocomplete
                placeholder="Parameter Path"
                selected={item.value.value}
                onInputChange={(value) => setParameterPathQuery(value)}
                renderOption={(option) => {
                  const item = parameterPathOptions.find(
                    (x) => x.key === option,
                  );

                  if (!item) {
                    return <>{option}</>;
                  }

                  return (
                    <>
                      <span>{item.key}</span>
                      <span className="text-sx ms-2 text-gray-600">
                        Example: {item.example}
                      </span>
                    </>
                  );
                }}
                onChange={(value) =>
                  onChange(
                    objectMapping.map((m) =>
                      m.key === item.key
                        ? {
                            ...m,
                            value: {
                              ...m.value,
                              type: 'param',
                              value: value || '',
                            },
                          }
                        : m,
                    ),
                  )
                }
                options={[
                  ...(parameterPathQuery ? [parameterPathQuery] : []),
                  ...(type === 'new_lead' ? leadKeys : eventKeys).map(
                    (x) => x.key,
                  ),
                ]}
              />
              <div className="flex flex-col gap-1">
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Default Value
                </label>
                <TypedTextField
                  placeholder="Default Value"
                  value={item.value.defaultValue}
                  onChange={(value) =>
                    onChange(
                      objectMapping.map((m) =>
                        m.key === item.key
                          ? {
                              ...m,
                              value: {
                                ...(m.value as any),
                                type: 'param',
                                defaultValue: value,
                              },
                            }
                          : m,
                      ),
                    )
                  }
                />
              </div>
              <Select
                placeholder="Data Type"
                selected={item.value.dataType ?? 'none'}
                onChange={(value) =>
                  onChange(
                    objectMapping.map((m) =>
                      m.key === item.key
                        ? {
                            ...m,
                            value: {
                              ...m.value,
                              type: 'param',
                              value: m.value.value,
                              dataType: value === 'none' ? null : 'date',
                            },
                          }
                        : m,
                    ),
                  )
                }
                options={[
                  { value: 'none', child: 'None' },
                  { value: 'date', child: 'Date' },
                ]}
              />
              {item.value.dataType === 'date' && (
                <>
                  <TextField
                    placeholder="Date Format"
                    value={item.value.dateFormat ?? ''}
                    onChange={(e) =>
                      onChange(
                        objectMapping.map((m) =>
                          m.key === item.key
                            ? {
                                ...m,
                                value: {
                                  ...m.value,
                                  dateFormat: e.target.value,
                                },
                              }
                            : m,
                        ),
                      )
                    }
                  />
                  <TextField
                    placeholder="Timezone"
                    value={item.value.timezone ?? ''}
                    onChange={(e) =>
                      onChange(
                        objectMapping.map((m) =>
                          m.key === item.key
                            ? {
                                ...m,
                                value: {
                                  ...m.value,
                                  timezone: e.target.value,
                                },
                              }
                            : m,
                        ),
                      )
                    }
                  />
                </>
              )}
            </>
          )}
          {item.value.type === 'map' && (
            <div className="flex w-full gap-2">
              <div className="h-full w-1 bg-gray-300" />
              <div className="flex-1">
                <ObjectMappingForm
                  type={type}
                  objectMapping={item.value.value}
                  onChange={(value) =>
                    onChange(
                      objectMapping.map((m) =>
                        m.key === item.key
                          ? {
                              ...m,
                              value: {
                                key: m.key,
                                type: 'map',
                                value,
                              },
                            }
                          : m,
                      ),
                    )
                  }
                />
              </div>
            </div>
          )}
        </div>
      ))}
      <div className="mt-2 flex w-full">
        <Button
          variant="secondary"
          className="w-full"
          icon={<PlusIcon className="h-4 w-4 text-black" />}
          onClick={() =>
            onChange([
              ...objectMapping,
              {
                key: '',
                value: {
                  type: 'custom',
                  value: '',
                },
              },
            ])
          }
        >
          Add Value
        </Button>
      </div>
    </div>
  );
};

interface TypedTextFieldProps {
  placeholder?: string;
  value: any;
  onChange: (value: any) => void;
}

const TypedTextField = ({
  placeholder,
  value,
  onChange,
}: TypedTextFieldProps) => {
  const type = useMemo(() => {
    return typeof value === 'string'
      ? 'string'
      : typeof value === 'number'
        ? 'number'
        : typeof value === 'boolean'
          ? 'boolean'
          : 'null';
  }, [value]);

  return (
    <div className="flex w-full items-center gap-4">
      <div className={type === 'null' ? 'w-full' : 'w-32'}>
        <Select<'string' | 'number' | 'boolean' | 'null'>
          placeholder="Type"
          selected={type}
          onChange={(value) =>
            onChange(
              value === 'string'
                ? ''
                : value === 'number'
                  ? 0
                  : value === 'boolean'
                    ? false
                    : null,
            )
          }
          options={[
            { value: 'string', child: 'String' },
            { value: 'number', child: 'Number' },
            { value: 'boolean', child: 'Boolean' },
            { value: 'null', child: 'Null' },
          ]}
        />
      </div>
      {type !== 'null' && (
        <div className="flex-1">
          {(type === 'string' || type === 'number') && (
            <TextField
              placeholder={placeholder || 'Value'}
              value={value}
              type={type === 'number' ? 'number' : 'text'}
              onChange={(e) => {
                if (type === 'number') {
                  onChange(parseFloat(e.target.value));
                } else {
                  onChange(e.target.value);
                }
              }}
            />
          )}
          {type === 'boolean' && (
            <Select<'true' | 'false'>
              placeholder={placeholder || 'Value'}
              className="w-full"
              selected={value ? 'true' : 'false'}
              onChange={(value) => onChange(value === 'true')}
              options={[
                { value: 'true', child: 'True' },
                { value: 'false', child: 'False' },
              ]}
            />
          )}
        </div>
      )}
    </div>
  );
};

const leadKeys = [
  { key: 'type', example: 'new_lead' },
  { key: 'data.accountId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.campaignId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.variantId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.websiteId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.formId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.form.name.value', example: 'John Doe' },
  { key: 'data.form.name.label', example: 'Name' },
  { key: 'data.form.email.value', example: 'john@example.com' },
  { key: 'data.form.email.label', example: 'Email' },
  { key: 'data.form.phone.value', example: 'xxxxxxxxxxx' },
  { key: 'data.form.phone.label', example: 'Phone' },
  { key: 'data.form.message.value', example: '' },
  { key: 'data.form.message.label', example: 'Message' },
  { key: 'data.url', example: 'https://example.page/xxxxxxxxxxx' },
  { key: 'data.userHash', example: 1000000000000000 },
  { key: 'data.sessionHash', example: 1000000000000000 },
  { key: 'data.id', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.queryParams.gclid', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.formName', example: 'Lead capture form' },
];

const eventKeys = [
  { key: 'type', example: 'new_event' },
  { key: 'data._id', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.createdOn', example: '2025-02-10T15:00:09.812Z' },
  { key: 'data.accountId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.campaignId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.variantId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.websiteId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.event', example: 'ping' },
  { key: 'data.path', example: '/xxxxxxxx' },
  { key: 'data.search', example: '?gclid=xxxxxx' },
  { key: 'data.title', example: 'Home' },
  { key: 'data.hash', example: 1000000000000000 },
  { key: 'data.sessionHash', example: 1000000000000000 },
  { key: 'data.visitHash', example: 1000000000000000 },
  { key: 'data.ip', example: '1.1.1.1' },
  { key: 'data.device', example: 'Desktop' },
  { key: 'data.os', example: 'Mac OS' },
  { key: 'data.browser', example: 'Chrome' },
  {
    key: 'data.userAgent',
    example:
      'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/132.0.0.0 Safari/537.36',
  },
  { key: 'data.origin', example: 'https://test.test.test' },
  { key: 'data.referrer', example: '' },
  { key: 'data.gclid', example: null },
  { key: 'data.shortHash', example: 'xxxxxxxxx' },
  { key: 'data.shortSessionHash', example: 'xxxxxxxxx' },
  { key: 'data.shortVisitHash', example: 'xxxxxxxxx' },
  { key: 'data.createdOnUnix', example: 10000000000 },
  { key: 'data.queryParams.gclid', example: 'xxxxxxxxxxxxxxxxxxxx' },
];
